import { FC, useEffect, useState } from "react";
import "./ModalAgentsCategoriesInsert.scss";
import ModalDefault from "../../../../components/ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormControl,
  FormGroup,
  InputGroup,
  Row,
} from "react-bootstrap";
import ErrorMessage from "../../../../components/ErrorMessage/ErrorMessage";
import { schema } from "./Validate";
import { useAgentsCategories } from "../../../../providers/Agents/AgentsCategories/AgentsCategoriesProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import InputTagsGroup from "../../../InputTagsGroup/InputTagsGroup";
import { useAgentsTerms } from "../../../../providers/Agents/Terms/AgentsTermsProvider";
import SkeletonTable from "../../../SkeletonTable/SkeletonTable";
import ReactSelect from "react-select";
import { customStyles } from "../../../../models/SelectCustomStyles";
import { PROMPT_TEMPLATE } from "../../../../enums/PromptTemplate";

interface ModalAgentsCategoriesInsertProps {
  show: boolean;
  onClose: () => void;
}

type FormValues = {
  id?: number;
  name?: string;
  description?: string;
  questions?: string[];
  terms?: string[];
  prompt_template?: string;
};

const ModalAgentsCategoriesInsert: FC<ModalAgentsCategoriesInsertProps> = ({
  show,
  onClose,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [clear, setClear] = useState<boolean>(false);
  const [questionIndexEdit, setQuestionIndexEdit] = useState<number | null>(
    null
  );
  const [question, setQuestion] = useState<string>("");
  const [questions, setQuestions] = useState<string[]>([]);
  const [termsRegistered, setTermsRegistered] = useState<any[]>([]);
  const [modelNameOptions] = useState<any | null>([
    { value: "gpt-3.5-turbo", label: "gpt-3.5-turbo" },
    { value: "gpt-4o", label: "gpt-4o" },
    { value: "gpt-4o-mini", label: "gpt-4o-mini" },
    { value: "gpt-4o-nano", label: "gpt-4o-nano" },
    { value: "gpt-4.1", label: "gpt-4.1" },
    { value: "gpt-4.1-mini", label: "gpt-4.1-mini" },
    { value: "gpt-5", label: "gpt-5" },
    { value: "gpt-5-mini", label: "gpt-5-mini" },
    { value: "gpt-5-nano", label: "gpt-5-nano" },
  ]);
  const [selectedModelName, setSelectedModelName] = useState<any | null>(null);

  const {
    category,
    setCategory,
    handleList,
    params,
    setParams,
    handleFetch,
    isLoadingModal,
  } = useAgentsCategories();
  const { handleListTerms, terms } = useAgentsTerms();
  const { onInvalid } = useAuth();

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const clearFields = () => {
    setValue("name", "");
    setValue("description", "");
    setValue("prompt_template", PROMPT_TEMPLATE);
    setValue("questions", []);
    setValue("terms", []);
    setQuestions([]);
    setTermsRegistered([]);
    setValue(
      "model_name",
      modelNameOptions
        .filter((item: any) => item.value === "gpt-4o-mini")
        ?.shift()?.value || ""
    );
    setSelectedModelName(
      modelNameOptions
        .filter((item: any) => item.value === "gpt-4o-mini")
        ?.shift()
    );
  };

  useEffect(() => {
    if (show && category) {
      handleListTerms(category?.id);
    } else {
      reset();
      clearFields();
    }
  }, [show]);

  useEffect(() => {
    if (category?.id) {
      setValue("name", category?.name ?? "");
      setValue("description", category?.description ?? "");
      setValue("questions", category?.questions || []);
      setValue("terms", category?.terms || []);
      setValue("prompt_template", category?.prompt_template || "");
      setQuestions(category?.questions || []);
      setTermsRegistered(
        category?.terms?.map((item: any, key: number) => ({
          value: key,
          label: item,
        })) || []
      );
      setSelectedModelName(
        modelNameOptions
          .filter((item: any) => item.value === category?.model_name)
          ?.shift() || null
      );
    }
  }, [category]);

  useEffect(() => {
    setValue("model_name", selectedModelName?.value || null);
  }, [selectedModelName]);
  useEffect(() => {
    setValue("questions", questions);
  }, [questions]);

  useEffect(() => {
    termsRegistered?.length > 0 &&
      setValue(
        "terms",
        termsRegistered.map((item) => item.label)
      );
  }, [termsRegistered]);

  const handleAddQuestion = () => {
    if (question) {
      if (questionIndexEdit !== null) {
        const updatedQuestions = [...questions];
        updatedQuestions[questionIndexEdit] = question;
        setQuestions(updatedQuestions);
        setQuestionIndexEdit(null);
      } else {
        setQuestions([...questions, question]);
      }
      setQuestion("");
    }
  };

  const handleDeleteQuestion = (index: number) => {
    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja deletar a questão: <b>${questions[index]}?</b></span>`,
      icon: "question",
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        setQuestions(questions.filter((_, i) => i !== index));
        toast.success("Questão deletada!");
      }
    });
  };

  const handleClose = () => {
    reset();
    clearFields();
    setCategory(null as any);
    onClose();
  };

  const onSubmit = async (data: any) => {
    setIsLoadingSubmit(true);

    if (category) {
      const upt = {
        id: category.id,
        name: data.name,
        description: data.description,
        questions: data.questions,
        terms: data.terms,
        model_name: data.model_name,
        prompt_template: data.prompt_template,
      };

      const ret = await handleFetch(upt);

      if (ret) {
        toast.success("Categoria alterada com sucesso!");
        handleList();
        handleClose();
      }
    } else {
      const ret = await handleFetch(data as any);

      if (ret) {
        toast.success("Categoria salva com sucesso!");
        handleList();
        handleClose();
      }
    }

    setIsLoadingSubmit(false);
  };

  const handleTags = (e: any) => {
    setTermsRegistered(e);
  };

  const handleClearInputTags = (e: any) => {
    if (e) {
      setClear(false);
      setTermsRegistered([]);
    }
  };

  return (
    <ModalDefault
      title={!!category?.id ? "Editar Categoria" : "Adicionar Categoria"}
      show={show}
      onClose={handleClose}
      sizeModal={"lg"}
      showFooter={true}
      buttonText={isLoadingSubmit ? "Salvando..." : "Salvar"}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div
        className="ModalAgentsCategoriesInsert"
        data-testid="ModalAgentsCategoriesInsert"
      >
        {!isLoadingModal ? (
          <>
            <Row>
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="name">NOME *</label>
                  <input
                    id="name"
                    className={`form-control ${onInvalid(errors?.name)}`}
                    type="text"
                    {...register("name")}
                    autoFocus
                  />
                  <ErrorMessage
                    name={"Nome"}
                    type={errors?.name?.type?.toString()}
                    min={3}
                    max={100}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="description">DESCRIÇÃO *</label>
                  <textarea
                    rows={5}
                    id="description"
                    className={`form-control ${onInvalid(errors?.description)}`}
                    {...register("description")}
                  />
                  <ErrorMessage
                    name={"Descrição"}
                    type={errors?.description?.type?.toString()}
                    min={3}
                    max={1000}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup className="form-group">
                  <label htmlFor="model_name">MODELO *</label>
                  <ReactSelect
                    isSearchable
                    isClearable
                    options={modelNameOptions ?? []}
                    value={selectedModelName}
                    placeholder="Selecione"
                    className={`form-control p-0 ${onInvalid(
                      errors?.model_name
                    )}`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      setSelectedModelName(e);
                    }}
                  />
                  <ErrorMessage
                    name={"Modelo"}
                    type={errors?.model_name?.type?.toString()}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="prompt_template">PROMPT *</label>
                  <textarea
                    rows={11}
                    id="prompt_template"
                    className={`form-control ${onInvalid(
                      errors?.prompt_template
                    )}`}
                    {...register("prompt_template")}
                    style={{ resize: "block" }}
                  />
                  <ErrorMessage
                    name={"Prompt"}
                    type={errors?.prompt_template?.type?.toString()}
                    min={3}
                    max={50000}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="terms">TERMOS *</label>
                  <InputTagsGroup
                    clear={clear}
                    isEdit={false}
                    onValue={(e: any) => handleTags(e)}
                    className={`${onInvalid(errors?.terms)}`}
                    valueInput={termsRegistered}
                    clearInput={(e: any) => handleClearInputTags(e)}
                  />
                  <ErrorMessage
                    name={"Termos"}
                    isArray
                    min={1}
                    type={errors?.terms?.type?.toString()}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="questions">QUESTÕES EXEMPLOS *</label>
                  <InputGroup className={`${onInvalid(errors?.questions)}`}>
                    <FormControl
                      id="questions"
                      value={question}
                      type="text"
                      onChange={(e) => setQuestion(e.target.value)}
                    />
                    <Button
                      variant="primary mb-0"
                      onClick={() => handleAddQuestion()}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </Button>
                  </InputGroup>
                  <ErrorMessage
                    name={"Questões"}
                    isArray
                    min={1}
                    type={errors?.questions?.type?.toString()}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Card>
              <CardBody>
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-100">Questão</th>
                      <th className="text-center" style={{ width: "1%" }}>
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions?.length > 0 ? (
                      questions.map((q, index) => (
                        <tr key={index} className="text-sm">
                          <td className="text-wrap">{q}</td>
                          <td className="text-center text-nowrap">
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="cursor-pointer"
                              onClick={() => {
                                setQuestion(q);
                                setQuestionIndexEdit(index);
                              }}
                            />
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="ms-2 cursor-pointer"
                              onClick={() => handleDeleteQuestion(index)}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} className="text-center text-sm pt-2">
                          Nenhuma questão adicionada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </>
        ) : (
          <SkeletonTable />
        )}
      </div>
    </ModalDefault>
  );
};

export default ModalAgentsCategoriesInsert;
